import { useState, useCallback, useRef, createContext, useContext } from 'react'
import type { ReactNode } from 'react'

export type ToastType = 'info' | 'warn' | 'error'

interface ToastItem {
  id: string
  message: string
  type: ToastType
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, duration?: number) => void
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} })

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  return useContext(ToastContext)
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback((message: string, type: ToastType = 'info', duration?: number) => {
    const id = crypto.randomUUID()
    const ms = duration ?? (type === 'error' ? 7000 : 4000)
    setToasts((prev) => [...prev, { id, message, type }])
    const timer = setTimeout(() => dismiss(id), ms)
    timers.current.set(id, timer)
  }, [dismiss])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className='kh-toast-stack'>
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`kh-toast kh-toast--${t.type}`}
            onMouseEnter={() => {
              const timer = timers.current.get(t.id)
              if (timer) { clearTimeout(timer); timers.current.delete(t.id) }
            }}
            onMouseLeave={() => {
              const timer = setTimeout(() => dismiss(t.id), 2000)
              timers.current.set(t.id, timer)
            }}
          >
            <span className='kh-toast-msg'>{t.message}</span>
            <button className='kh-toast-close' onClick={() => dismiss(t.id)}>✕</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
